import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useReadLocalStorage } from 'usehooks-ts'

import usePrompt from '@shared/hooks/src/usePrompt'
import { BuildEnv, handleError, Logger, VoiceCallStatus } from '@shared/utils'

import { useCall, useToken, useTwilioCall, useTwilioDevice } from './VoiceCallProvider.hooks'

const VoiceCallStateContext = createContext(null)
const log = Logger('VoiceCallProvider')

export function useVoiceCallState() {
  const context = useContext(VoiceCallStateContext)
  if (!context) {
    throw new Error('useVoiceCallState must be used within the VoiceCallProvider')
  }
  return context
}

export function useVoiceCall() {
  const callState = useVoiceCallState()
  return callState.call.data
}

/**
 * VoiceCallProvider is responsible for creating the Twilio Voice Device and managing the active call.
 * It also provides the device and call information to the rest of the application.
 *
 * @param {String} externalCall - Indication that call is external and outside the application, so no user id or appointment id is needed
 * @param {String} userId - User id to make the call
 * @param {String} appointmentId - Appointment id to make the call
 * @param {Boolean} disableHistory - Disables the call history for the call
 * @param {Boolean} allowInAppNavigation - Allows in-app navigation without a prompt
 * @param {ReactNode} children - React children
 *
 * @returns {JSX.Element} - VoiceCallStateContext.Provider with next information:
 * - makeCall: function to make a call
 * - hangUp: function to end a call
 * - error: any error that occurred
 * - twilio: {
 *   - device: Twilio Voice Device
 *   - call: Twilio Voice Call
 *   - callSid: Twilio Voice Call Sid
 * }
 */
export default function VoiceCallProvider({
  externalCall,
  userId,
  appointmentId,
  disableHistory = false,
  allowInAppNavigation = false,
  children,
}) {
  const [lastPhoneNumber, setLastPhoneNumber] = useState('')
  const phoneOverride = useReadLocalStorage('voice-phone-override')

  const { data: token, error: tokenError, fetchToken } = useToken({ userId, externalCall })

  // Errors flow through a ref so the hook handlers can call the latest updateCall
  // even though updateCall is created later in this body.
  const updateCallRef = useRef(null)
  const handleError_ = useCallback((e) => {
    handleError(e)
    updateCallRef.current?.({ status: VoiceCallStatus.Failed })
  }, [])

  const device = useTwilioDevice({ token, fetchToken, onError: handleError_ })
  const { callSid, call: twilioCall, connect, disconnect } = useTwilioCall({ device, onError: handleError_ })
  const [call, updateCall] = useCall({ userId, appointmentId, externalCall, callSid, disableHistory })

  useEffect(() => {
    updateCallRef.current = updateCall
  }, [updateCall])

  const error = tokenError || call.error

  const makeCall = useCallback(
    async (to, params = {}) => {
      if (error) {
        handleError(error, { message: 'Call could not be completed, please try again later' })
        return updateCall({ status: VoiceCallStatus.Failed })
      }

      if (!device) return

      const phone = import.meta.env.VITE_BUILD_ENV !== BuildEnv.Production && phoneOverride ? phoneOverride : to
      setLastPhoneNumber(phone)

      log.info('Voice Call initiation')
      updateCall({ status: VoiceCallStatus.Initiated, endTime: undefined, startTime: undefined })

      try {
        await connect(phone, params)
        log.info('Voice Call initiated')
      } catch (e) {
        log.error('Voice Call failed', e)
        updateCall({ status: VoiceCallStatus.Failed })

        // Safety net: tokenWillExpire should already have refreshed proactively,
        // but if we ever land here we refresh in place — no device rebuild.
        if (['AccessTokenInvalid', 'AccessTokenExpired'].includes(e.name)) {
          toast('Your Voice Call token expired — refreshing. Please try again.')
          try {
            const fresh = await fetchToken()
            await device.updateToken(fresh)
          } catch (refreshError) {
            log.error('Voice Call token refresh failed', refreshError)
          }
        }
      }
    },
    [error, device, phoneOverride, updateCall, connect, fetchToken]
  )

  const hangUp = useCallback(() => {
    if (call.data?.status === VoiceCallStatus.InProgress) {
      updateCall({ status: VoiceCallStatus.Completed, endTime: new Date().toUTCString() })
    } else {
      updateCall({ status: VoiceCallStatus.NoAnswer })
    }

    disconnect()
  }, [call.data?.status, disconnect, updateCall])

  // Prevent the user from leaving the page while a call is in progress.
  usePrompt('A call is in progress, are you sure you want to leave?', Boolean(twilioCall), allowInAppNavigation)

  const state = useMemo(
    () => ({
      call,
      makeCall,
      hangUp,
      error,
      lastPhoneNumber,
      twilio: {
        device,
        call: twilioCall,
        callSid,
      },
    }),
    [call, makeCall, hangUp, error, lastPhoneNumber, device, twilioCall, callSid]
  )

  return <VoiceCallStateContext.Provider value={state}>{children}</VoiceCallStateContext.Provider>
}
